'use client';

import { cn } from '@utils/cn';
import { usePathname } from 'next/navigation';
import { FC, ReactNode, useEffect } from 'react';
import { MobileHeaderVariant, usePageChrome } from './page-chrome-context';
import { MOBILE_CHROME_BREAKPOINT, useIsMobile } from '@hooks/use-mobile';

type PageHeaderProps = {
  title: string;
  description?: string;
  /** The one acid-lime button per view belongs here. */
  actions?: ReactNode;
  /**
   * `responsive` hides the title below the desktop chrome breakpoint, where the
   * mobile navbar already shows it. `always` is required on routes with no
   * sidebar (`/legal`, `/welcome`), which have no `@container/chrome` ancestor
   * and would otherwise never reveal the title.
   */
  showTitle?: 'responsive' | 'always';
  /** Optional explicit parent for deep-linked mobile screens. */
  backHref?: string;
  backLabel?: string;
  mobileVariant?: MobileHeaderVariant;
  hideMobileTabs?: boolean;
  /** Moves `actions` into the native mobile navigation bar. */
  actionsInMobileHeader?: boolean;
  /** Breadcrumbs and sync/status context sit between copy and actions. */
  breadcrumbs?: ReactNode;
  status?: ReactNode;
  className?: string;
  children?: ReactNode;
};

const PageHeader: FC<PageHeaderProps> = ({
  title,
  description,
  actions,
  showTitle = 'responsive',
  backHref,
  backLabel,
  mobileVariant,
  hideMobileTabs,
  actionsInMobileHeader = false,
  breadcrumbs,
  status,
  className,
  children
}) => {
  const pathname = usePathname();
  const { register } = usePageChrome();
  const isMobile = useIsMobile(MOBILE_CHROME_BREAKPOINT);

  useEffect(
    () =>
      register({
        pathname,
        title,
        backHref,
        backLabel,
        mobileVariant,
        status,
        trailingAction: actionsInMobileHeader ? actions : undefined,
        hideMobileTabs
      }),
    [
      backHref,
      backLabel,
      actions,
      actionsInMobileHeader,
      hideMobileTabs,
      mobileVariant,
      pathname,
      register,
      status,
      title
    ]
  );

  return (
    <div
      className={cn(
        'mb-6 flex flex-wrap items-start justify-between gap-3 @lg/chrome:mb-7',
        className
      )}
    >
      <div className="min-w-0 space-y-1">
        {breadcrumbs}
        <div className="flex items-center gap-2">
          <h1
            className={cn(
              'text-page-title text-foreground',
              // The mobile chrome renders the visible title. Keep one semantic
              // h1 here so screen readers never receive duplicate headings.
              showTitle === 'responsive' && 'sr-only @lg/chrome:not-sr-only'
            )}
          >
            {title}
          </h1>
          {status}
        </div>
        {description && (
          <p className="max-w-prose text-caption text-muted-foreground">
            {description}
          </p>
        )}
        {children}
      </div>
      {actions && (!actionsInMobileHeader || !isMobile) && (
        <div
          className={cn(
            'flex items-center gap-2',
            actionsInMobileHeader && 'hidden @lg/chrome:flex'
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
